import { useReveal } from "../../hooks/useReveal";
import { Link } from "react-router-dom";
import { ParallaxBg } from "../../components/ParallaxBg";
import { Accordion } from "../../components/Accordion";
import { R_FAQS, R_HOST } from "../../lib/replayCopy";
import { COMPLIANCE_DISCLOSURE } from "../../lib/copy";
import { scrollToBook } from "./ReplaySections";

const CREDS = [
  { k: "COMPANY", v: R_HOST.company },
  { k: "NMLS#", v: R_HOST.nmls },
  { k: "CO. NMLS#", v: R_HOST.coNmls },
  { k: "LICENSED IN", v: R_HOST.licensed },
];

export function RHost() {
  const ref = useReveal();
  return (
    <section id="host" ref={ref} className="relative overflow-hidden py-28 md:py-36" data-testid="replay-host-section">
      <ParallaxBg image="/bg/home1.jpg" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <p className="section-label mb-14 reveal-up">[004] — YOUR HOST</p>
        <div className="grid lg:grid-cols-[420px_1fr] gap-14 items-start">
          <div className="host-panel reveal-up" data-testid="replay-host-panel">
            <img src="/matthew.jpg" alt="Matthew Lake-Nestor" className="host-photo" data-testid="replay-host-headshot" />
            <span className="corner tl" /><span className="corner tr" /><span className="corner bl" /><span className="corner br" />
            <div className="host-nameplate" data-testid="replay-host-nameplate">
              <p className="font-extrabold text-sm uppercase tracking-wide">Matthew Lake-Nestor</p>
              <p className="mono" style={{ fontSize: 9, letterSpacing: "0.18em", color: "rgba(240,236,228,0.55)" }}>MORTGAGE ADVISOR | TEAM LEAD · PROPERTY LENDING TEAM</p>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-4">MEET MATTHEW LAKE-NESTOR</p>
            <h2 className="reveal-up font-black uppercase mb-8" style={{ fontSize: "clamp(26px, 3vw, 42px)", letterSpacing: "-0.02em", lineHeight: 1.08 }}>
              MORTGAGE GUIDANCE FROM SOMEONE WHO UNDERSTANDS THE <span style={{ color: "#3D7BFF" }}>WHOLE</span> PROCESS.
            </h2>
            <div className="reveal-up mb-8" style={{ width: 64, height: 2, background: "#3D7BFF" }} />
            <div className="space-y-5 reveal-up mb-10" style={{ color: "rgba(240,236,228,0.62)", lineHeight: 1.8, fontSize: 15 }} data-testid="replay-host-bio">
              <p>
                I'm Matthew Lake-Nestor, a licensed Mortgage Loan Advisor and Realtor. I've developed a particular passion for helping Veterans and military families understand and make the most of the home loan benefit they've earned. Having helped hundreds of families understand their financing options and navigate the mortgage process with confidence, I'm excited about the opportunity to help you do the same. My real-estate experience gives me a unique perspective that many mortgage professionals don't have: I understand how financing decisions can affect the offer, the contract, the appraisal, the closing timeline, and ultimately a buyer's ability to get the keys.
              </p>
              <p>
                A successful home purchase takes coordination. Your Realtor has an important role in helping you find and negotiate the right home, while your Mortgage Loan Advisor is responsible for helping you understand and secure the financing. My job is to communicate clearly, anticipate financing issues, and work alongside your real-estate professional so everyone is working toward the same goal—a smooth, successful closing.
              </p>
              <p>
                That's the approach behind Property Lending Team: educate first, communicate clearly, and make sure you understand the numbers before you make major decisions. You've seen the training — your free strategy call is where we apply it to your situation, so you're better prepared when it's time to make your move.
              </p>
              <p>
                And if you don't already have a Realtor, I can help connect you with the right real-estate professional—someone who understands the VA homebuying process and shares our commitment to communication, coordination, and taking great care of you throughout the transaction.
              </p>
            </div>
            <div className="cred-grid reveal-up mb-10" data-testid="replay-host-credentials">
              {CREDS.map((c) => (
                <div className="cred-cell" key={c.k}>
                  <p className="k">{c.k}</p>
                  <p className="v">{c.v}</p>
                </div>
              ))}
            </div>
            <div className="reveal-up">
              <button className="btn-accent" data-testid="replay-host-cta" onClick={scrollToBook}>✓ BOOK MY FREE STRATEGY CALL</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const BOOK_BULLETS = [
  "We verify your VA eligibility and entitlement together, live",
  "You see your funding fee, possible exemptions, and real closing costs",
  "You leave with a written plan, whether the timing is now or later",
];

export function RObjections() {
  const ref = useReveal();
  return (
    <section ref={ref} className="py-28 md:py-36" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="objections-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <div>
          <p className="section-label mb-8 reveal-up">[005] — STILL DECIDING?</p>
          <h2 className="reveal-up font-black uppercase mb-8" style={{ fontSize: "clamp(26px, 3vw, 40px)", letterSpacing: "-0.02em" }}>
            STILL ON THE FENCE?
          </h2>
          <div className="space-y-5 reveal-up" style={{ color: "rgba(240,236,228,0.6)", lineHeight: 1.8, fontSize: 15 }} data-testid="objection-paragraphs">
            <p>
              "Sellers won't choose a VA offer over a cash or conventional buyer." That's the myth — and you saw the truth in the training. The strength of any offer depends on the circumstances, but an experienced lending team can help your Realtor understand the financing, anticipate potential issues, and confidently communicate the strength of your financing to the Listing Agent and Seller — helping put your Realtor in a stronger position to negotiate and get your offer accepted.
            </p>
            <p>
              "What if I'm told 'not yet' again?" Then this time you'll hear it with numbers instead of a shrug. The eligibility review either shows a path that works now, or it shows precisely what needs to change and by how much — no hard credit pull required. Either way, you trade "someday" for a written plan. Not knowing is the only outcome that costs you.
            </p>
          </div>
        </div>
        <div className="lg:pt-24">
          <p className="mono reveal-up mb-8" style={{ fontSize: 12, letterSpacing: "0.22em", color: "#3D7BFF" }}>
            HERE'S WHAT HAPPENS WHEN YOU BOOK:
          </p>
          <ul className="space-y-6 mb-12">
            {BOOK_BULLETS.map((t, i) => (
              <li key={i} className="reveal-up flex gap-4 items-start" data-testid={`book-bullet-${i + 1}`}>
                <span style={{ color: "#3D7BFF", fontWeight: 800, fontSize: 18 }}>→</span>
                <span style={{ color: "rgba(240,236,228,0.8)", lineHeight: 1.7, fontSize: 15 }}>{t}</span>
              </li>
            ))}
          </ul>
          <div className="reveal-up">
            <button className="btn-accent" data-testid="objections-cta" onClick={scrollToBook}>✓ BOOK MY FREE STRATEGY CALL</button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function RFaq() {
  const ref = useReveal();
  const items = R_FAQS.map((f) => ({ title: f.q, body: f.a }));
  return (
    <section id="faq" ref={ref} className="relative overflow-hidden py-28 md:py-36" style={{ background: "rgba(255,255,255,0.015)" }} data-testid="replay-faq-section">
      <ParallaxBg image="/bg/home2.jpg" />
      <div className="relative max-w-[900px] mx-auto px-6 lg:px-12">
        <p className="section-label mb-8 reveal-up">[006] — FAQ</p>
        <h2 className="reveal-up font-black uppercase mb-14" style={{ fontSize: "clamp(28px, 3.6vw, 48px)", letterSpacing: "-0.02em" }}>
          COMMON QUESTIONS
        </h2>
        <div className="reveal-up">
          <Accordion items={items} testPrefix="replay-faq" />
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  "Free educational call — no obligation",
  "No loan application required",
  "No hard credit pull",
  "Same-week response, personally from Matthew",
];

export function RFinalBooking() {
  const ref = useReveal();
  return (
    <section id="book" ref={ref} className="final-section py-28 md:py-40" data-testid="final-booking-section">
      <div className="final-glow" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <p className="section-label mb-10 reveal-up">[007] — BOOK YOUR CALL</p>
          <h2 className="reveal-up font-black uppercase mb-12" style={{ fontSize: "clamp(34px, 4.4vw, 60px)", letterSpacing: "-0.03em", lineHeight: 0.98 }}>
            YOUR NEXT MOVE STARTS WITH A CLEARER <span style={{ color: "#3D7BFF" }}>MAP.</span>
          </h2>
          <ul className="space-y-4">
            {TRUST.map((t, i) => (
              <li key={i} className="reveal-up flex gap-3 items-center text-sm md:text-base" style={{ color: "rgba(240,236,228,0.75)" }} data-testid={`booking-trust-${i + 1}`}>
                <span style={{ color: "#3D7BFF", fontWeight: 800 }}>✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="glass-card p-7 md:p-9 reveal-up" data-testid="final-booking-card">
          <p className="eyebrow mb-2">FREE VA BUYER PRE-APPROVAL STRATEGY CALL</p>
          <h3 className="font-extrabold text-xl mb-5">Lock in your free strategy call with Matthew</h3>
          <p className="text-center mt-4" style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", letterSpacing: "0.08em" }}>
            Free · No hard credit pull · Written plan included
          </p>
        </div>
      </div>
    </section>
  );
}

export function RFooter() {
  return (
    <footer className="relative pt-24 pb-40 overflow-hidden border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} data-testid="replay-footer">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid md:grid-cols-3 gap-12 mb-16">
          <div>
            <img src="/nexa-logo.png" alt="Nexa Lending" className="mb-6" style={{ height: 44, width: "auto" }} />
            <p className="eyebrow mb-5">LICENSING</p>
            <div className="space-y-1 text-xs" style={{ color: "rgba(255,255,255,0.45)", lineHeight: 1.9 }}>
              <p className="font-bold" style={{ color: "rgba(240,236,228,0.8)" }}>Matthew Lake-Nestor</p>
              <p>Nexa Lending</p>
              <p>NMLS# {R_HOST.nmls}</p>
              <p>Company NMLS# {R_HOST.coNmls}</p>
              <p>Licensed in {R_HOST.licensed}</p>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-5">CONTACT</p>
            <div className="space-y-1 text-xs" style={{ color: "rgba(255,255,255,0.45)", lineHeight: 1.9 }}>
              <p><i className="fa-solid fa-phone mr-2" style={{ color: "rgba(61,123,255,0.6)" }}></i>980-298-1440</p>
              <p><i className="fa-solid fa-envelope mr-2" style={{ color: "rgba(61,123,255,0.6)" }}></i>mlake@nexalending.com</p>
              <p><i className="fa-solid fa-globe mr-2" style={{ color: "rgba(61,123,255,0.6)" }}></i>[[AWAITING DEDICATED LENDER WEBSITE/DOMAIN]]</p>
            </div>
          </div>
          <div>
            <p className="eyebrow mb-5">LEGAL</p>
            <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            <Link to="/terms" className="footer-link">Terms of Use</Link>
          </div>
        </div>
        <div className="flex gap-4 items-start mb-10 pt-10 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <span style={{ fontSize: 26, color: "rgba(255,255,255,0.4)", lineHeight: 1 }} title="Equal Housing Lender">⌂</span>
          <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", lineHeight: 1.8, maxWidth: 1000 }} data-testid="replay-compliance">
            {COMPLIANCE_DISCLOSURE}
          </p>
        </div>
        <p className="mono" style={{ fontSize: 10, letterSpacing: "0.18em", color: "rgba(255,255,255,0.18)" }} data-testid="replay-copyright">
          © 2026 NEXA LENDING · MATTHEW LAKE-NESTOR · WAM.WEBINAR v2.0 · REPLAY
        </p>
      </div>
      <div className="footer-wordmark">SEE THE WHOLE DEAL</div>
    </footer>
  );
}
